/**
 * Phase D drag-and-drop for same-slide block moves, persisting to the slide's
 * `bodyHtml`. The event contract also represents palette inserts, but the
 * caller intentionally does not materialise new blocks yet.
 *
 * ADR-002 (`thread/docs/deck-editor/ADR-002-direct-manipulation-editing.md`).
 *
 * This uses the same `data-edit-path` source map as `useInPlaceEdit` (see
 * `./README.md`) and is wired into `DeckEditor`:
 *
 *   - Each draggable block is an element with `data-edit-path` (a block-level
 *     run; the renderer marks container blocks the same way it marks text
 *     runs — see the README contract).
 *   - HTML5 drag events fire inside the same-origin iframe document. On drop
 *     we compute an intended mutation (move block A before/after block B, or
 *     insert a new block from the palette) expressed purely in terms of
 *     edit-paths, and hand it to `onMutate`.
 *   - The caller turns a move `BlockMutation` into a new `bodyHtml` (splice the
 *     serialized block subtree at the target edit-path) and PUTs the slide
 *     → `cacheTag` reload re-renders the moved DOM. NO live DOM reordering is
 *     done here; `bodyHtml` is the single source of truth (ADR-002).
 *
 * Palette insert events are typed but remain a documented follow-up because
 * no palette UI or template-to-HTML materialiser is wired.
 */

import { useCallback, useRef } from "react";

import {
  closestSlideIdx,
  EDIT_PATH_ATTR,
  SLIDE_RENDERED_EVENT,
} from "./useInPlaceEdit";

/**
 * Selector for every **draggable** node in a rendered slide: any element
 * carrying a `data-edit-path`, regardless of `data-edit-kind`. (Phase C's
 * editable selector is text-only; here both text runs and block containers
 * are drag sources / drop targets — the caller's `applyBlockMutation`
 * validates whether a given source/target/position is legal against the
 * edit-path schema.) The slide *root* the runtime renders `bodyHtml` into
 * never carries the attribute, so scoping a query to it (or to the document
 * body) naturally excludes the fragment root, per the README contract.
 */
const DRAGGABLE_SELECTOR = `[${EDIT_PATH_ATTR}]`;

/** Where a dropped block lands relative to the target edit-path node. */
export type DropPosition = "before" | "after" | "into";

/** A block move: relocate the block at `sourcePath` near `targetPath`. */
export interface BlockMoveMutation {
  kind: "move";
  /** edit-path of the block being dragged. */
  sourcePath: string;
  /** edit-path of the block being dropped onto. */
  targetPath: string;
  position: DropPosition;
  /**
   * Slide the move happens within, from the target's `[data-slide-idx]`
   * section (the plain `edit` layout shows every slide at once — FXC-8818).
   * Omitted when no such ancestor exists (single-slide layouts), where the
   * caller applies the mutation to its active slide. edit-paths are per-slide,
   * so a move only ever stays within one slide's `bodyHtml`.
   */
  slideIdx?: number;
}

/** A new-block insert dragged in from a palette (not yet in `bodyHtml`). */
export interface BlockInsertMutation {
  kind: "insert";
  /** Opaque template id from the block palette (resolved to HTML by caller). */
  templateId: string;
  /** edit-path of the block being dropped onto. */
  targetPath: string;
  position: DropPosition;
  /** Slide the insert targets (see `BlockMoveMutation.slideIdx`). */
  slideIdx?: number;
}

/** The intended `bodyHtml` mutation emitted on a successful drop. */
export type BlockMutation = BlockMoveMutation | BlockInsertMutation;

/** Persist callback — caller applies the mutation to `bodyHtml` + PUTs. */
export type OnBlockMutate = (mutation: BlockMutation) => void;

export interface UseBlockDndOptions {
  /** The same-origin preview iframe (see `useInPlaceEdit` for rationale). */
  getIframe: () => HTMLIFrameElement | null;
  /** Called with the intended mutation on drop; DeckEditor persists moves. */
  onMutate: OnBlockMutate;
  /** Master switch (default true). */
  enabled?: boolean;
  /**
   * MIME type used to carry a palette template id during a cross-surface
   * drag (palette in the parent → drop in the iframe). For in-slide block
   * moves the source edit-path is read from the dragged element directly.
   */
  paletteMime?: string;
}

export interface UseBlockDndApi {
  /** Attach drag/drop listeners to the current iframe document. */
  attach(): void;
  /** Detach listeners (call before the iframe reloads / on unmount). */
  detach(): void;
}

export const DEFAULT_PALETTE_MIME = "application/x-thread-block-template";

/** Marker class added to the drop-target block while a drag is over it. */
export const DROP_TARGET_CLASS = "thread-block-drop-target";

/**
 * Marker class added to every draggable block when DnD is active. The
 * integration's stylesheet hangs the grab cursor (and any hover affordance)
 * off this class; we also set it as a hook so a re-render that replaces the
 * slide DOM can be re-marked idempotently.
 */
export const DRAGGABLE_CLASS = "thread-block-draggable";

/**
 * Mark every draggable node within `scope` as an HTML5 drag source. Setting
 * `draggable="true"` is the load-bearing fix: without it the browser fires
 * no `dragstart`/`dragover`/`drop` at all. Idempotent — safe to re-run on
 * every `thread-slide-rendered` (the runtime swaps the slide subtree, so the
 * fresh nodes need re-marking).
 */
function markDraggable(scope: ParentNode): void {
  const nodes = scope.querySelectorAll<HTMLElement>(DRAGGABLE_SELECTOR);
  nodes.forEach((el) => {
    el.setAttribute("draggable", "true");
    el.classList.add(DRAGGABLE_CLASS);
  });
}

/**
 * Decide whether the pointer is in the top, middle, or bottom third of the
 * target block — maps to before / into / after. `into` is only meaningful
 * for container blocks; the caller validates against the edit-path schema.
 */
function dropPositionFor(el: HTMLElement, clientY: number): DropPosition {
  const rect = el.getBoundingClientRect();
  const offset = clientY - rect.top;
  const third = rect.height / 3;
  if (offset < third) return "before";
  if (offset > third * 2) return "after";
  return "into";
}

export function useBlockDnd(options: UseBlockDndOptions): UseBlockDndApi {
  const {
    getIframe,
    onMutate,
    enabled = true,
    paletteMime = DEFAULT_PALETTE_MIME,
  } = options;

  const onMutateRef = useRef<OnBlockMutate>(onMutate);
  onMutateRef.current = onMutate;

  // edit-path of the block currently being dragged from within the slide.
  const draggingPathRef = useRef<string | null>(null);
  // Slide the drag started in (plain ``edit`` layout — FXC-8818), so a drop can
  // refuse a cross-slide move. null in single-slide layouts.
  const draggingSlideIdxRef = useRef<number | null>(null);
  const currentTargetRef = useRef<HTMLElement | null>(null);
  const attachedDocRef = useRef<Document | null>(null);
  const attachedWinRef = useRef<Window | null>(null);

  const clearTarget = useCallback(() => {
    currentTargetRef.current?.classList.remove(DROP_TARGET_CLASS);
    currentTargetRef.current = null;
  }, []);

  const onDragStart = useCallback(
    (ev: Event) => {
      const target = (ev.target as HTMLElement | null)?.closest<HTMLElement>(
        DRAGGABLE_SELECTOR,
      );
      const path = target?.getAttribute(EDIT_PATH_ATTR) ?? null;
      draggingPathRef.current = path;
      draggingSlideIdxRef.current = target ? closestSlideIdx(target) : null;
      const dt = (ev as DragEvent).dataTransfer;
      if (dt && path) {
        dt.effectAllowed = "move";
        dt.setData("text/plain", path);
      }
    },
    [],
  );

  const onDragOver = useCallback(
    (ev: Event) => {
      const dragEv = ev as DragEvent;
      const overBlock = (ev.target as HTMLElement | null)?.closest<HTMLElement>(
        DRAGGABLE_SELECTOR,
      );
      if (!overBlock) return;
      ev.preventDefault(); // allow drop
      if (currentTargetRef.current !== overBlock) {
        clearTarget();
        overBlock.classList.add(DROP_TARGET_CLASS);
        currentTargetRef.current = overBlock;
      }
      if (dragEv.dataTransfer) dragEv.dataTransfer.dropEffect = "move";
    },
    [clearTarget],
  );

  const onDrop = useCallback(
    (ev: Event) => {
      const dragEv = ev as DragEvent;
      ev.preventDefault();
      const targetBlock = (ev.target as HTMLElement | null)?.closest<HTMLElement>(
        DRAGGABLE_SELECTOR,
      );
      const targetPath = targetBlock?.getAttribute(EDIT_PATH_ATTR) ?? null;
      const position = targetBlock
        ? dropPositionFor(targetBlock, dragEv.clientY)
        : "after";
      // Slide the drop landed in (plain ``edit`` layout — FXC-8818). null in
      // single-slide layouts, where ``slideIdx`` is omitted and the caller
      // applies the mutation to its active slide.
      const targetSlideIdx = targetBlock ? closestSlideIdx(targetBlock) : null;
      const sourceSlideIdx = draggingSlideIdxRef.current;
      draggingSlideIdxRef.current = null;
      clearTarget();
      if (!targetPath) return;
      const slideIdxField =
        targetSlideIdx !== null ? { slideIdx: targetSlideIdx } : {};

      const dt = dragEv.dataTransfer;
      const templateId = dt?.getData(paletteMime) || "";
      if (templateId) {
        // Cross-surface drag: a new block from the palette.
        onMutateRef.current({
          kind: "insert",
          templateId,
          targetPath,
          position,
          ...slideIdxField,
        });
        return;
      }

      const sourcePath =
        draggingPathRef.current ?? dt?.getData("text/plain") ?? null;
      draggingPathRef.current = null;
      if (!sourcePath || sourcePath === targetPath) return;
      // edit-paths are per-slide, so a block move only makes sense within one
      // slide's ``bodyHtml``. If source and target are demonstrably different
      // slides, refuse it — v1 has no cross-slide block move.
      if (
        sourceSlideIdx !== null &&
        targetSlideIdx !== null &&
        sourceSlideIdx !== targetSlideIdx
      ) {
        return;
      }
      onMutateRef.current({
        kind: "move",
        sourcePath,
        targetPath,
        position,
        ...slideIdxField,
      });
    },
    [clearTarget, paletteMime],
  );

  // Re-mark draggable nodes on every (re)render: the runtime replaces the
  // slide subtree on navigation + on the cacheTag reload, so the fresh nodes
  // arrive without our `draggable="true"`. The event carries the new `root`.
  const onSlideRendered = useCallback((ev: Event) => {
    const detail = (ev as CustomEvent<{ root?: HTMLElement }>).detail;
    const scope = detail?.root ?? attachedDocRef.current?.body ?? null;
    if (scope) markDraggable(scope);
  }, []);

  const detach = useCallback(() => {
    const doc = attachedDocRef.current;
    const win = attachedWinRef.current;
    if (win) win.removeEventListener(SLIDE_RENDERED_EVENT, onSlideRendered);
    attachedWinRef.current = null;
    if (!doc) return;
    doc.removeEventListener("dragstart", onDragStart, true);
    doc.removeEventListener("dragover", onDragOver, true);
    doc.removeEventListener("drop", onDrop, true);
    attachedDocRef.current = null;
    clearTarget();
  }, [clearTarget, onDragOver, onDragStart, onDrop, onSlideRendered]);

  const attach = useCallback(() => {
    if (!enabled) return;
    const iframe = getIframe();
    const win = iframe?.contentWindow ?? null;
    const doc = iframe?.contentDocument ?? win?.document ?? null;
    if (!doc || attachedDocRef.current === doc) return;
    detach();
    doc.addEventListener("dragstart", onDragStart, true);
    doc.addEventListener("dragover", onDragOver, true);
    doc.addEventListener("drop", onDrop, true);
    attachedDocRef.current = doc;
    // Re-mark on every subsequent slide render (and the cacheTag reload's
    // first render after rebind). Listener lives on the iframe window where
    // the runtime dispatches the event.
    if (win) {
      win.addEventListener(SLIDE_RENDERED_EVENT, onSlideRendered);
      attachedWinRef.current = win;
    }
    // Mark whatever is already mounted (we may attach after the first
    // render, before any `thread-slide-rendered` we'd see).
    markDraggable(doc.body);
  }, [detach, enabled, getIframe, onDragOver, onDragStart, onDrop, onSlideRendered]);

  return { attach, detach };
}
